import type {
  CustomFieldDefinition,
  CustomFieldValue,
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { LegacyClientDetailPresentation } from '../../presentations';
import { convertClientFeatureFlags } from '../convertToClientFeatureFlags';
import { formatCustomFieldValue, formatGiftPlan, formatNumberOfPeople } from '../formater';

/**
 * LegacyClientDetailPresentation に渡す props へ変換する際の引数オブジェクト
 */
type Args = {
  /** クライアント詳細 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
  /** カスタムフィールド定義一覧 */
  customFieldDefinitions: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
  /** パートナーフィーチャーフラグ一覧 */
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
};

type PresentationProps = Pick<
  React.ComponentProps<typeof LegacyClientDetailPresentation>,
  'clientDetail'
>;

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * カスタムフィールド値をラベルでグルーピングする
 *
 * 同じラベルを持つ複数のカスタムフィールド値（複数選択の場合など）を
 * カンマ区切りの文字列として1つにまとめる。
 *
 * @param customFieldValues - カスタムフィールド値の配列
 * @returns ラベルをキー、値（カンマ区切り）をバリューとするMap
 *
 * @example
 * groupCustomFieldValuesByLabel([
 *   { label: '趣味', value: 'reading' },
 *   { label: '趣味', value: 'sports' },
 *   { label: '部署', value: 'sales' }
 * ])
 * // => Map { '趣味' => 'reading,sports', '部署' => 'sales' }
 *
 * @remarks
 * 現在のAPIスキーマでは CustomFieldValue に custom_field_definition_id が存在しないため、
 * label をキーとしてグルーピングしています。この制約により、以下のケースでは
 * 誤ったマッチングが発生する可能性があります：
 * - 同名ラベルが存在する場合
 * - ラベルが変更された場合
 * - i18n等で表示ラベルが変わる場合
 *
 * 将来的にAPIスキーマが拡張され、CustomFieldValue に定義IDが含まれるようになった場合は、
 * IDベースのマッチングに移行することを推奨します。
 */
const groupCustomFieldValuesByLabel = (
  customFieldValues: CustomFieldValue[]
): Map<string, string> => {
  const grouped = new Map<string, string>();

  for (const { label, value } of customFieldValues) {
    const existing = grouped.get(label);
    grouped.set(label, existing ? `${existing},${value.toString()}` : value.toString());
  }

  return grouped;
};

/**
 * カスタムフィールドを表示用の配列に変換する
 *
 * カスタムフィールド定義と値を元に、Presentationコンポーネントで表示するための
 * ID・ラベル・表示値の配列を生成する。
 *
 * @param customFieldDefinitions - カスタムフィールド定義の配列
 * @param customFieldValues - カスタムフィールド値の配列（nullの場合あり）
 * @returns 表示用のカスタムフィールド配列（{ id, label, value }の配列）
 */
const convertCustomFields = (
  customFieldDefinitions: CustomFieldDefinition[],
  customFieldValues: CustomFieldValue[] | null
): PresentationProps['clientDetail']['customFields'] => {
  if (!customFieldValues || customFieldValues.length === 0 || customFieldDefinitions.length === 0) {
    return [];
  }
  const groupedValues = groupCustomFieldValuesByLabel(customFieldValues);

  return customFieldDefinitions.map(field => ({
    id: field.id,
    label: field.label,
    value: formatCustomFieldValue(field, groupedValues.get(field.label)),
  }));
};

/**
 * APIレスポンスをPresentationコンポーネントのpropsに変換する
 *
 * @param args.data - クライアント詳細APIのレスポンスデータ
 * @param args.customFieldDefinitions - カスタムフィールド定義一覧
 * @param args.partnerFeatureFlags - パートナーフィーチャーフラグ一覧
 * @returns clientDetail - Presentation用に整形されたクライアント詳細情報
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  customFieldDefinitions,
  partnerFeatureFlags,
}) => {
  const visibleClientFeatureFlags = data.client.client_feature_flags ?? [];

  return {
    clientDetail: {
      id: data.client.id,
      clientName: data.client.name,
      customFields: convertCustomFields(
        customFieldDefinitions.custom_field_definitions,
        data.client.custom_field_values
      ),
      contactPersonName: data.client.contact_person_name || '-',
      contactPersonEmail: data.client.contact_person_email || '-',
      contactPersonPhone: data.client.contact_person_phone || '-',
      corporateNumber: data.client.corporate_number || '-',
      externalClientId: data.client.external_client_id || '-',
      groupName:
        data.client.groups && data.client.groups.length > 0
          ? data.client.groups.map(g => g.name).join(', ')
          : '-',
      giftPlan: formatGiftPlan(data.client.gift_plan),
      employeeCount: formatNumberOfPeople(data.client.employee_count),
      userCount: formatNumberOfPeople(data.client.user_count),
      nonInvitedUserCount: formatNumberOfPeople(data.client.non_invited_user_count),
      createdAt: formatDate({
        date: new Date(data.client.created_at * 1000),
        formatType: 'yyyy/MM/dd H:mm',
      }),
      updatedAt: formatDate({
        date: new Date(data.client.updated_at * 1000),
        formatType: 'yyyy/MM/dd H:mm',
      }),
      clientFeatureFlags: convertClientFeatureFlags(
        visibleClientFeatureFlags,
        partnerFeatureFlags.partner_feature_flags
      ),
    },
  };
};
